<template>
  <div v-click-outside="handleClose" class="stickers-upsell-quantity">
    <div class="stickers-upsell-quantity__select" @click="handleOpen">
      <template v-if="displayValue.label">
        {{ displayValue.label }}
      </template>
      <div v-else class="stickers-upsell-quantity__option stickers-upsell-quantity__option--inline">
        <span class="stickers-upsell-quantity__option-quantity"
          >{{ displayValue.quantity }} stickers</span
        >
        <span class="stickers-upsell-quantity__option-price"
          >${{ displayValue.unit_price_total }}</span
        >
        <span v-if="displayValue.percentage" class="stickers-upsell-quantity__option-discount"
          >Save {{ displayValue.percentage }}%</span
        >
      </div>
      <Spinner v-if="isFetching && !isCustom" theme="dark" class="stickers-upsell-quantity__icon" />
      <UseSvg
        v-else
        :class="{
          'stickers-upsell-quantity__icon': true,
          'stickers-upsell-quantity__icon--opened': isOpened
        }"
        icon="chevron-down"
      />
    </div>

    <template v-if="isCustom">
      <div class="stickers-upsell-quantity__field">
        <input type="tel" class="stickers-upsell-quantity__input" @input="handleChange" />
        <div v-if="pricing || isFetching" class="stickers-upsell-quantity__pricing">
          <Spinner v-if="isFetching" theme="dark" class="stickers-upsell-quantity__spinner" />
          <template v-else>
            <div>
              <div v-if="pricing.percentage" class="stickers-upsell-quantity__pricing-old">
                ${{ pricing.retail_price_total }}
              </div>
              <div class="stickers-upsell-quantity__pricing-new">
                ${{ pricing.unit_price_total }}
              </div>
            </div>
            <div v-if="pricing.percentage" class="stickers-upsell-quantity__pricing-discount">
              {{ pricing.percentage }}%
            </div>
          </template>
        </div>
      </div>
      <div
        class="stickers-upsell-quantity__hint"
        :class="{ 'stickers-upsell-quantity__hint-error': !isQuantityValid }"
      >
        {{ message }}
      </div>
    </template>

    <div v-if="isOpened" class="stickers-upsell-quantity__options">
      <div
        v-for="option in options"
        :key="option.key"
        class="stickers-upsell-quantity__option"
        @click="handleSelect(option)"
      >
        <span v-if="option.key === 'custom'" class="stickers-upsell-quantity__option-custom">
          {{ option.label }}
        </span>
        <template v-else>
          <span class="stickers-upsell-quantity__option-quantity"
            >{{ option.quantity }} stickers</span
          >
          <span class="stickers-upsell-quantity__option-price">${{ option.unit_price_total }}</span>
          <span v-if="option.percentage" class="stickers-upsell-quantity__option-discount"
            >Save {{ option.percentage }}%</span
          >
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import debounce from "lodash.debounce";
import { api } from "jiffy-api";
import UseSvg from "common/vue/components/UseSvg.vue";
import Spinner from "components/jiffy/vue-cart/Spinner.vue";
import { MIN_QUANTITY } from "./constants";

const prepareQuantity = quantity => ({
  ...quantity,
  percentage: Math.round(quantity.percentage || 0),
  unit_price_total: Number(quantity.unit_price_total || quantity.retail_price_total || 0).toFixed(
    2
  ),
  retail_price_total: Number(quantity.retail_price_total || 0).toFixed(2)
});

const props = defineProps({
  value: Number,
  artwork: Object,
  size: [Object, null]
});

const emit = defineEmits(["change"]);

const isOpened = ref(false);
const isCustom = ref(false);
const isFetching = ref(false);
const pricing = ref(null);
const quantities = ref([]);

const options = computed(() => [
  ...quantities.value.map((quantity, index) => ({
    key: index,
    isCustom: false,
    ...quantity
  })),
  { isCustom: true, key: "custom", label: "Custom Quantity" }
]);

const isQuantityValid = computed(() => !props.value || props.value >= MIN_QUANTITY);

const message = computed(() => {
  if (isQuantityValid.value) {
    return `Minimum order is ${MIN_QUANTITY}.`;
  }

  return `Please enter at least ${MIN_QUANTITY}.`;
});

const displayValue = computed(() => {
  if (isCustom.value) return { label: "Custom Quantity" };
  if (!props.value) return { label: "Select Quantity" };

  const selectedOption = options.value.find(option => option.quantity === props.value);
  if (selectedOption) return selectedOption;

  return { label: "Select Quantity" };
});

const loadQuantities = async () => {
  try {
    isFetching.value = true;
    const response = await api.dtf.getStickersQuantities({
      artworks: [
        {
          artwork_id: props.artwork.id,
          width: props.size.size.width,
          height: props.size.size.height
        }
      ]
    });
    quantities.value = (response.quantities || []).map(prepareQuantity);
  } finally {
    isFetching.value = false;
  }
};

const loadPricingDedounced = debounce(async newValue => {
  try {
    isFetching.value = true;
    pricing.value = null;

    const prices = await api.dtf.getStickersPrices({
      artworks: [
        {
          artwork_id: props.artwork.id,
          width: props.size.size.width,
          height: props.size.size.height,
          quantity: newValue
        }
      ]
    });
    pricing.value = prepareQuantity(prices);
  } finally {
    isFetching.value = false;
  }
}, 500);

const handleOpen = () => {
  isOpened.value = true;
};

const handleClose = () => {
  isOpened.value = false;
};

const handleSelect = option => {
  isCustom.value = option.isCustom;
  pricing.value = null;
  emit("change", option.isCustom ? null : option.quantity);
  handleClose();
};

const handleChange = event => {
  const { value } = event.target;
  const quantity = value.replace(/[^0-9]/g, "").replace(/^0/, "");
  const newValue = Number(quantity);

  event.target.value = quantity;

  emit("change", newValue);
  if (newValue >= MIN_QUANTITY) {
    loadPricingDedounced(newValue);
  }
};

watch(
  () => props.size,
  async newValue => {
    if (newValue && newValue.size) {
      await loadQuantities();
      handleSelect(quantities.value[0]);
    }
  },
  { immediate: true }
);
</script>

<style lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/jiffy/settings";

.stickers-upsell-quantity {
  position: relative;
  color: $text-black;

  &__select {
    padding: 10px 36px 10px 12px;
    border-radius: 4px;
    border: 1px solid $gray-label;
    background: $white;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.42;
    cursor: pointer;
    position: relative;
  }

  &__icon {
    position: absolute;
    width: 12px;
    height: 12px;
    right: 12px;
    top: 50%;
    margin-top: -6px;

    &--opened {
      transform: rotate(180deg);
    }
  }

  &__options {
    position: absolute;
    z-index: 1;
    bottom: 0;
    left: 0;
    right: 0;
    margin-top: 6px;
    border-radius: 4px;
    border: 1px solid $gray-label;
    background: $white;
    box-shadow:
      0 4px 8px -2px rgba(18, 25, 38, 0.1),
      0 2px 4px -2px rgba(18, 25, 38, 0.06);

    @include with-tablet {
      top: 40px;
      bottom: auto;
    }
  }

  &__option {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.42;

    &--inline {
      padding: 0;
    }
  }

  &__option-quantity {
    flex-shrink: 0;
    width: 100px;
  }

  &__option-price {
    flex-shrink: 0;
    width: 65px;
    font-weight: 700;
  }

  &__option-discount {
    flex: 1;
    color: $green-emerald;
    max-width: 150px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__option-custom {
    color: $primary-light;
    font-weight: 700;
  }

  &__field {
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__input {
    appearance: none;
    min-width: 120px;
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid $gray-label;
    background: $white;
    color: $text-black;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.42;
    outline: none;

    &:focus {
      border-color: $info;
    }
  }

  &__pricing {
    flex: 1;
    display: flex;
    gap: 8px;
    align-items: flex-end;
  }

  &__spinner {
    width: 20px;
  }

  &__pricing-old {
    color: $warm-grey;
    font-size: 14px;
    font-weight: 400;
    line-height: 0.85;
    text-decoration: line-through;
  }

  &__pricing-new {
    color: $text-black;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.11;
  }

  &__pricing-discount {
    padding: 2px 4px;
    color: $white;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.66;
    border-radius: 4px;
    background: $green-emerald;
  }

  &__hint {
    margin-top: 4px;
    color: $warm-grey;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.1;
  }

  &__hint-error {
    color: $color-error;
  }
}
</style>
